import firebase from '../config/firebase.config';
import { DEFAULT_WORKING_DAYS } from '../utils/attendanceUtils';

const database = firebase.firestore();

const DEFAULT_WORKING_SCHEDULE = {
  days: DEFAULT_WORKING_DAYS,
  startTime: '09:30 AM',
  endTime: '06:30 PM',
};

async function addNewCompany(payload) {
  const personalId = Math.random().toString(36).substring(2, 32);
  try {
    const batch = database.batch();
    const companyRef = database.collection('companyDatabase').doc();
    const memberRef = database
      .collection('membersDatabase')
      .doc(payload.createdBy.userId);
    const teamDBRef = database.collection('teamDatabase').doc();
    const companyTeamsRef = database
      .collection('companyDatabase')
      .doc(companyRef?.id)
      .collection('teams')
      .doc(teamDBRef.id);
    const cityRef = database.collection('companyDatabase').doc(companyRef?.id);
    const doc = await cityRef.get();
    const companyTeam = doc.data();

    // Calculate trial expiry date (14 days from now)
    const trialExpiryDate = new Date();
    trialExpiryDate.setDate(trialExpiryDate.getDate() + 14);

    // Add data to company Database (without subscription fields)
    batch.set(
      companyRef,
      {
        name: payload.companyDetails.name,
        // size: payload.companyDetails.size,
        location: payload.companyDetails.location,
        timeZone: payload.companyDetails.timezone,
        phoneNumber: payload.companyDetails.phoneNumber,
        screenshot: true,
        createdBy: payload.createdBy.userId,
        admins: [payload.createdBy.userId],
        subscriptions: {
          trialExpiryDate: trialExpiryDate.toISOString(),
          isTrialActive: true,
          occupiedSeats: 1,
        },
      },
      { merge: true }
    );

    // Update team details
    batch.set(
      teamDBRef,
      {
        teamName: 'Personal',
        ownedBy: companyRef.id,
        createdBy: payload.createdBy.userId,
        memberDetails: {
          [payload.createdBy.userId]: {
            userEmailid: payload.createdBy.email,
            userName: payload.createdBy.name,
            userType: payload.createdBy.role,
          },
        },
      },
      { merge: true }
    );
    // Add data to member database
    batch.set(
      memberRef,
      {
        accountEmailid: payload.createdBy.email,
        accountFullName: payload.createdBy.name,
      },
      { merge: true }
    );

    // Add data to company list of member
    const memberCompanyListRef = memberRef
      .collection('companyList')
      .doc(companyRef.id);
    batch.set(
      memberCompanyListRef,
      {
        // Company creator is company-wide privileged (Super Admin), not a
        // team-scoped 'Admin' label — see src/hooks/usePermission.js. This is
        // separate from payload.createdBy.role, which stays 'Admin' for the
        // team-level writes below (userType / teamList.Role).
        companyRole: 'SuperAdmin',
        companyName: payload.companyDetails.name,
        companyId: companyRef.id,
      },
      { merge: true }
    );
    // Add team to teamList in related CompanyList
    const memberTeamListRef = memberCompanyListRef
      .collection('teamList')
      .doc(teamDBRef.id);
    batch.set(
      memberTeamListRef,
      {
        Role: payload.createdBy.role,
        Team: 'Personal',
        email: payload.createdBy.email,
        activeTeamid: memberTeamListRef.id,
      },
      { merge: true }
    );
    // ** Updates to the companyDatabase
    batch.set(
      companyTeamsRef,
      {
        teamId: teamDBRef.id,
        // members: membersIDs.length > 0 ? membersIDs : {},
        // ...(membersIDs.length > 0 ? { members: membersIDs } : {})
      },
      { merge: true }
    );

    batch.update(cityRef, {
      teams: [teamDBRef.id],
    });
    return batch
      .commit()
      .then(() => {
        const functions = firebase.functions();
        const sendEmailForNewCompanyCreated = functions.httpsCallable(
          'sendEmailForNewCompanyCreated'
        );
        sendEmailForNewCompanyCreated({
          userEmail: payload.createdBy.email,
          userName: payload.createdBy.name,
          companyName: payload.companyDetails.name,
          companyId: companyRef.id,
          phoneNumber: payload.companyDetails.phoneNumber,
        });
        return {
          success: true,
          data: {
            message: 'Company created successfully!',
            data: { id: companyRef.id, ...payload.companyDetails.name },
          },
        };
      })
      .catch((e) => {
        throw e;
      });
  } catch (error) {
    throw error;
  }
}

async function getCompanySubscriptionStatus(companyId) {
  try {
    const companyDoc = await database
      .collection('companyDatabase')
      .doc(companyId)
      .get();

    if (!companyDoc.exists) {
      throw new Error('Company not found');
    }

    const companyData = companyDoc.data();

    // Get subscription data from subcollection
    const subscriptionDoc = await database
      .collection('companyDatabase')
      .doc(companyId)
      .get();
    let subscriptionData = {};
    if (subscriptionDoc.exists && subscriptionDoc.data()?.subscriptions) {
      subscriptionData = subscriptionDoc.data()?.subscriptions;
    } else {
      // Default subscription data if subcollection doesn't exist
      subscriptionData = {
        status: 'trial',
        trialExpiryDate: null,
        subscriptionExpiryDate: null,
        stripeCustomerId: null,
        stripeSubscriptionId: null,
        currentPlan: null,
        billingCycle: null,
        lastPaymentStatus: null,
        nextPaymentAttempt: null,
      };
    }
    return {
      ...subscriptionData,
      companyId,
      subscriptionStatus: subscriptionData?.status || 'trial',
      trialExpiryDate: subscriptionData?.trialExpiryDate || null,
      subscriptionExpiryDate: subscriptionData?.subscriptionExpiryDate || null,
      stripeCustomerId: subscriptionData?.stripeCustomerId || null,
      stripeSubscriptionId: subscriptionData?.stripeSubscriptionId || null,
      currentPlan: subscriptionData?.currentPlan || null,
      billingCycle: subscriptionData?.billingCycle || null,
      lastPaymentStatus: subscriptionData?.lastPaymentStatus || null,
      nextPaymentAttempt: subscriptionData?.nextPaymentAttempt || null,
      companyName: companyData?.name || '',
    };
  } catch (error) {
    console.error('Error fetching subscription status:', error);
    return {
      companyId,
      subscriptionStatus: 'trial', // Default fallback
      trialExpiryDate: null,
      subscriptionExpiryDate: null,
      stripeCustomerId: null,
      stripeSubscriptionId: null,
      currentPlan: null,
      billingCycle: null,
      lastPaymentStatus: null,
      nextPaymentAttempt: null,
      companyName: '',
    };
  }
}
async function getCompaniesWithSubscriptionStatus(companyList) {
  try {
    const companiesWithSubscription = await Promise.all(
      companyList.map(async (company) => {
        const companyDoc = await database
          .collection('companyDatabase')
          .doc(company.companyId)
          .get();

        // Get subscription data from subcollection

        let subscriptionData = {};
        if (companyDoc.exists && companyDoc.data()?.subscriptions) {
          subscriptionData = companyDoc.data()?.subscriptions;
        } else {
          subscriptionData = {
            status: 'trial',
            trialExpiryDate: null,
            subscriptionExpiryDate: null,
          };
        }

        return {
          ...company,
          subscriptionStatus: subscriptionData?.status || 'trial',
          trialExpiryDate: subscriptionData?.trialExpiryDate || null,
          subscriptionExpiryDate:
            subscriptionData?.subscriptionExpiryDate || null,
        };
      })
    );

    return {
      success: true,
      data: companiesWithSubscription,
    };
  } catch (error) {
    console.error('Error fetching companies subscription status:', error);
    return {
      success: false,
      data: companyList, // Fallback to original data
      error: error.message,
    };
  }
}

async function getCompanyTimezone(companyId) {
  try {
    if (!companyId) {
      return {
        success: false,
        timezone: null,
        error: 'Company ID is required',
      };
    }

    const companyDoc = await database
      .collection('companyDatabase')
      .doc(companyId)
      .get();

    if (!companyDoc.exists) {
      return {
        success: false,
        timezone: null,
        error: 'Company not found',
      };
    }

    const companyData = companyDoc.data();
    const timezone = companyData?.timeZone || null;

    return {
      success: true,
      timezone: timezone,
    };
  } catch (error) {
    console.error('Error fetching company timezone:', error);
    return {
      success: false,
      timezone: null,
      error: error.message,
    };
  }
}

async function getWorkingSchedule(companyId) {
  try {
    if (!companyId) {
      return {
        success: false,
        ...DEFAULT_WORKING_SCHEDULE,
        error: 'Company ID is required',
      };
    }

    const companyDoc = await database
      .collection('companyDatabase')
      .doc(companyId)
      .get();

    const workingSchedule = companyDoc.data()?.workingSchedule;

    return {
      success: true,
      days: workingSchedule?.days || DEFAULT_WORKING_SCHEDULE.days,
      startTime:
        workingSchedule?.startTime || DEFAULT_WORKING_SCHEDULE.startTime,
      endTime: workingSchedule?.endTime || DEFAULT_WORKING_SCHEDULE.endTime,
    };
  } catch (error) {
    console.error('Error fetching working schedule:', error);
    return {
      success: false,
      ...DEFAULT_WORKING_SCHEDULE,
      error: error.message,
    };
  }
}

async function updateWorkingSchedule({
  companyId,
  days,
  startTime,
  endTime,
  updatedBy,
}) {
  try {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    await database
      .collection('companyDatabase')
      .doc(companyId)
      .set(
        {
          workingSchedule: {
            days,
            startTime,
            endTime,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedBy,
          },
        },
        { merge: true }
      );

    return { success: true };
  } catch (error) {
    console.error('Error updating working schedule:', error);
    return { success: false, error: error.message };
  }
}

async function updateStealthMode({ companyId, enabled }) {
  try {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    await database.collection('companyDatabase').doc(companyId).set(
      {
        stealthMode: enabled,
      },
      { merge: true }
    );

    return { success: true };
  } catch (error) {
    console.error('Error updating stealthMode:', error);
    return { success: false, error: error.message };
  }
}

async function updateScreenshotsEnabled({ companyId, enabled }) {
  try {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    await database.collection('companyDatabase').doc(companyId).set(
      {
        screenshot: enabled,
      },
      { merge: true }
    );

    return { success: true };
  } catch (error) {
    console.error('Error updating screenshot setting:', error);
    return { success: false, error: error.message };
  }
}

async function updateAlertConfig({
  companyId,
  enabled,
  productivityPercentageThreshold,
  lateLoginAlertMinutes,
  updatedBy,
}) {
  try {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    await database
      .collection('companyDatabase')
      .doc(companyId)
      .set(
        {
          alertConfig: {
            enabled,
            productivityPercentageThreshold,
            lateLoginAlertMinutes,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedBy,
          },
        },
        { merge: true }
      );

    return { success: true };
  } catch (error) {
    console.error('Error updating alert config:', error);
    return { success: false, error: error.message };
  }
}

async function updateLeavePolicy({
  companyId,
  paidLeaveAllocation,
  updatedBy,
}) {
  try {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    const currentYear = new Date().getFullYear();
    const docRef = database
      .collection('companyDatabase')
      .doc(companyId)
      .collection('leavePolicy')
      .doc(currentYear.toString());

    const doc = await docRef.get();
    const isNewDocument = !doc.exists;

    const policyData = {
      year: currentYear,
      paidLeaveAllocation,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedBy,
      companyId,
    };

    if (isNewDocument) {
      policyData.createdAt = firebase.firestore.FieldValue.serverTimestamp();
      policyData.createdBy = updatedBy;
    }

    await docRef.set(policyData, { merge: true });

    return { success: true };
  } catch (error) {
    console.error('Error updating leave policy:', error);
    return { success: false, error: error.message };
  }
}

async function updateHolidays({ companyId, year, holidays, updatedBy }) {
  try {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    await database
      .collection('companyDatabase')
      .doc(companyId)
      .collection('holidays')
      .doc(year.toString())
      .set(
        {
          year,
          holidays,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedBy,
        },
        { merge: true }
      );

    return { success: true };
  } catch (error) {
    console.error('Error updating holidays:', error);
    return { success: false, error: error.message };
  }
}

export default {
  addNewCompany,
  getCompanySubscriptionStatus,
  getCompaniesWithSubscriptionStatus,
  getCompanyTimezone,
  getWorkingSchedule,
  updateWorkingSchedule,
  updateStealthMode,
  updateScreenshotsEnabled,
  updateAlertConfig,
  updateLeavePolicy,
  updateHolidays,
};
